/**
 * Human durations, for copy.
 *
 * Distinct from `src/core/time/timestamp.ts`, which formats cue times from
 * Ticks in a subtitle dialect. Everything here takes SECONDS from the API and
 * produces the strings docs/USER_JOURNEY.md interpolates.
 *
 * The rounding rules are asymmetric on purpose, and the asymmetry is the whole
 * point of this file:
 *
 *   * What the user HAS left rounds DOWN. Saying "12 minutes left" when 12.9
 *     remain is a small under-promise; saying it when 11.2 remain is a lie
 *     that turns into E8 one click later.
 *   * What something WILL COST rounds UP. "uses about 6 of your daily minutes"
 *     must not become 7 after the fact.
 *
 * Both directions err toward the user being told a number they will not be
 * surprised by, which is the same rule the quota copy follows in prose.
 */

/** Whole minutes remaining. Rounds DOWN — never overstate what is left. */
export function minutesRemaining(seconds: number): number {
  return Math.max(0, Math.floor(seconds / 60));
}

/** Whole minutes a job will cost. Rounds UP — never understate a cost. */
export function minutesCost(seconds: number): number {
  return Math.max(1, Math.ceil(seconds / 60));
}

/**
 * `h:mm:ss` above an hour, `m:ss` below it — the shape E7's "{2:14:30}" and
 * E8's "{41:20}" both take. Not zero-padded on the leading unit, because
 * "0:41:20" is not how anyone says forty-one minutes.
 */
export function clock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor(total / 60) % 60;
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** The transcript gutter's `[m:ss]`, without the brackets. */
export function stamp(seconds: number): string {
  return clock(seconds);
}

/**
 * The rolling-window reset, split into the `{3}h {05}m` the copy expects.
 *
 * `null` in, `null` out. contracts/api.md is explicit that `resets_in_s` is
 * null — not zero — when the session has no debits in the window: there is
 * nothing to reset, and "resets in 0h 0m" is the exact bug that null exists to
 * prevent. Callers must handle null rather than defaulting it.
 */
export function resetsIn(seconds: number | null): { h: string; m: string } | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return null;
  const total = Math.round(seconds);
  return {
    h: String(Math.floor(total / 3600)),
    m: String(Math.floor(total / 60) % 60).padStart(2, '0'),
  };
}

/** Bytes as the size warning shows them. Binary units, one decimal past MB. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

/**
 * A filename that is safe to put in the DOM as text and short enough not to
 * blow up a banner. Truncation happens in the middle so the extension — the
 * part that explains the error — survives.
 */
export function shortFilename(name: string, max = 48): string {
  if (name.length <= max) return name;
  const keepEnd = Math.min(16, Math.floor(max / 3));
  return `${name.slice(0, max - keepEnd - 1)}…${name.slice(-keepEnd)}`;
}
